import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

type TeeTimeConfigRow = Database['public']['Tables']['tee_time_configs']['Row'];
type TeeTimeBlockRow = Database['public']['Tables']['tee_time_blocks']['Row'];
type BookingRow = Database['public']['Tables']['bookings']['Row'];

export interface GeneratedTeeTimeSlot {
  configId: string;
  configName: string;
  startTime: string; // ISO timestamp
  endTime: string; // ISO timestamp
  maxPlayers: number; // owner-configured group size limit
  isBooked: boolean; // true if any active booking exists for this slot
  priceCents: number;
  pricingType: 'per_player' | 'per_round';
  bookingType: 'auto' | 'request';
  displayTime: string; // formatted like "9:00 AM"
  displayDate: string; // formatted like "Oct 29"
}

function parseTime(timeStr: string): { hours: number; minutes: number } {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return { hours, minutes };
}

function formatDisplayTime(date: Date, timezone: string): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone
  });
  return formatter.format(date);
}

function formatDisplayDate(date: Date, timezone: string): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: timezone
  });
  return formatter.format(date);
}

function getLocalDateString(date: Date, timezone: string): string {
  // Format date in the specified timezone as YYYY-MM-DD
  const formatter = new Intl.DateTimeFormat('en-CA', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit',
    timeZone: timezone 
  });
  return formatter.format(date);
}

function getLocalDayOfWeek(date: Date, timezone: string): number {
  // Get day of week (0-6) in the specified timezone
  const zonedDate = toZonedTime(date, timezone);
  const formatter = new Intl.DateTimeFormat('en-US', { 
    weekday: 'long',
    timeZone: timezone 
  });
  const weekdayName = formatter.format(zonedDate);
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days.indexOf(weekdayName);
}

function getMonday(date: Date, timezone: string): string {
  const zonedDate = toZonedTime(date, timezone);
  const day = getLocalDayOfWeek(date, timezone);
  const diff = zonedDate.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(zonedDate);
  monday.setDate(diff);
  return getLocalDateString(monday, timezone);
}

function isSlotBlocked(
  slotStart: Date,
  slotEnd: Date,
  date: Date,
  blocks: TeeTimeBlockRow[],
  configId: string,
  timezone: string
): boolean {
  const dateStr = getLocalDateString(date, timezone);
  const mondayStr = getMonday(date, timezone);
  const slotStartStr = slotStart.toISOString();
  const slotEndStr = slotEnd.toISOString();

  for (const block of blocks) {
    if (block.tee_time_config_id !== configId || block.status !== 'scheduled') {
      continue;
    }

    // Check week block
    if (block.scope === 'week' && block.week_monday === mondayStr) {
      return true;
    }

    // Check day block
    if (block.scope === 'day' && block.day === dateStr) {
      return true;
    }

    // Check slot block (timestamp overlap)
    if (block.scope === 'slot' && block.slot_start && block.slot_end) {
      const blockStart = new Date(block.slot_start);
      const blockEnd = new Date(block.slot_end);
      
      // Check if there's any overlap
      if (slotStart < blockEnd && slotEnd > blockStart) {
        return true;
      }
    }
  }

  return false;
}

export async function generateTeeTimesForCourse(
  courseId: string,
  selectedDateString: string // YYYY-MM-DD format
): Promise<GeneratedTeeTimeSlot[]> {
  // Fetch the course to get its timezone
  const { data: course, error: courseError } = await supabase
    .from('courses')
    .select('timezone')
    .eq('id', courseId)
    .single();

  if (courseError || !course) {
    console.error('Error fetching course:', courseError);
    return [];
  }

  const courseTimezone = course.timezone || 'America/Denver';

  // Fetch all active configs for this course
  const { data: configs, error: configsError } = await supabase
    .from('tee_time_configs')
    .select('*')
    .eq('course_id', courseId)
    .eq('status', 'active')
    .is('deleted_at', null);

  if (configsError) {
    console.error('Error fetching configs:', configsError);
    return [];
  }
  
  if (!configs || configs.length === 0) {
    return [];
  }

  const typedConfigs = configs as TeeTimeConfigRow[];

  // Fetch all scheduled blocks for this course
  const { data: blocks, error: blocksError } = await supabase
    .from('tee_time_blocks')
    .select('*')
    .eq('course_id', courseId)
    .eq('status', 'scheduled');

  if (blocksError) {
    console.error('Error fetching tee time blocks:', blocksError);
  }

  const allBlocks = (blocks as TeeTimeBlockRow[]) || [];

  // Fetch all bookings for the next 30 days for this course's configs
  const configIds = typedConfigs.map(c => c.id);
  const startOfRange = new Date();
  startOfRange.setHours(0, 0, 0, 0);
  const endOfRange = new Date(startOfRange);
  endOfRange.setDate(endOfRange.getDate() + 30);

  const { data: bookings, error: bookingsError } = await supabase
    .from('bookings')
    .select('tee_time_config_id, slot_start, qty')
    .in('tee_time_config_id', configIds)
    .gte('slot_start', startOfRange.toISOString())
    .lte('slot_start', endOfRange.toISOString())
    .in('status', ['confirmed', 'requested', 'reserved', 'paid']);

  // Build a set of booked slot keys (configId|slotStartISO)
  // A slot is booked if ANY active booking exists for it, regardless of group size
  const bookedSlots = new Set<string>();

  if (!bookingsError && bookings) {
    const typedBookings = bookings as Array<{ tee_time_config_id: string; slot_start: string; qty: number }>;
    for (const booking of typedBookings) {
      const normalizedSlotStart = new Date(booking.slot_start).toISOString();
      bookedSlots.add(`${booking.tee_time_config_id}|${normalizedSlotStart}`);
    }
  }

  // Generate slots for the selected date only
  const generatedSlots: GeneratedTeeTimeSlot[] = [];

  for (const config of typedConfigs) {
    // Use timezone from the course (fetched from courses table)
    const timezone = courseTimezone;
    
    // Use the date string directly - it represents a date in the course's timezone
    const dateStr = selectedDateString;
    
    // Create a Date object representing midnight in the course's timezone
    const zonedMidnight = fromZonedTime(`${dateStr}T00:00:00`, timezone);
    
    // Check if this config applies to this day of week IN THE COURSE'S TIMEZONE
    const dayOfWeek = getLocalDayOfWeek(zonedMidnight, timezone);
    if (!config.days_of_week.includes(dayOfWeek)) {
      continue;
    }

    // Parse start and end times
    const startTimeParts = parseTime(config.start_time);
    const endTimeParts = parseTime(config.end_time);

    // Create start and end times in the course's timezone, then convert to UTC
    const startTimeStr = `${dateStr}T${config.start_time}`;
    const endTimeStr = `${dateStr}T${config.end_time}`;
    
    let currentSlotUTC = fromZonedTime(startTimeStr, timezone);
    const endTimeUTC = fromZonedTime(endTimeStr, timezone);

    while (currentSlotUTC < endTimeUTC) {
      const slotEndUTC = new Date(currentSlotUTC);
      slotEndUTC.setMinutes(slotEndUTC.getMinutes() + config.interval_minutes);

      // Check if this slot is blocked (using UTC timestamps for comparison)
      if (isSlotBlocked(currentSlotUTC, slotEndUTC, zonedMidnight, allBlocks, config.id, timezone)) {
        currentSlotUTC = slotEndUTC;
        continue;
      }

      // A slot is booked if any active booking exists for it
      const bookingKey = `${config.id}|${currentSlotUTC.toISOString()}`;
      const isBooked = bookedSlots.has(bookingKey);

      // Show all future non-blocked slots — available and booked alike
      // (booked slots render greyed out so players know the time is taken)
      const now = new Date();
      if (currentSlotUTC > now) {
        generatedSlots.push({
          configId: config.id,
          configName: config.name,
          startTime: currentSlotUTC.toISOString(),
          endTime: slotEndUTC.toISOString(),
          maxPlayers: config.max_players_per_slot,
          isBooked,
          priceCents: config.price_cents,
          pricingType: config.pricing_type,
          bookingType: config.booking_type,
          displayTime: formatDisplayTime(currentSlotUTC, timezone),
          displayDate: formatDisplayDate(currentSlotUTC, timezone)
        });
      }

      currentSlotUTC = slotEndUTC;
    }
  }

  // Sort by start time
  generatedSlots.sort((a, b) => 
    new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  return generatedSlots;
}
